import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface PlanSelectorProps {
  userType: 'buyer' | 'seller';
  selectedPlan: 'monthly' | 'annual';
  onPlanChange: (plan: 'monthly' | 'annual') => void;
}

const PlanSelector = ({ userType, selectedPlan, onPlanChange }: PlanSelectorProps) => {
  const plans = userType === 'buyer' ? {
    monthly: { price: 9.99, label: '$9.99/month', oldLabel: undefined as string | undefined },
    annual: { price: 99.99, label: '$99.99/year', savings: '17%', oldLabel: undefined as string | undefined }
  } : {
    monthly: { price: 34.99, label: '$34.99/month', oldLabel: '$68' as string | undefined },
    annual: { price: 349.99, label: '$349.99/year', savings: '17%', oldLabel: '$680' as string | undefined }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">Select Your Plan</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Monthly Plan */}
        <Card
          className={cn(
            "p-6 cursor-pointer transition-all border-2",
            selectedPlan === 'monthly'
              ? "border-godyspo-gold bg-godyspo-gold/10"
              : "border-muted hover:border-godyspo-gold/50"
          )}
          onClick={() => onPlanChange('monthly')}
        >
          <div className="flex justify-between items-start mb-4">
            <div>
              <h4 className="text-lg font-semibold text-white">Monthly</h4>
              <p className="text-2xl font-bold text-godyspo-gold mt-2">
                {plans.monthly.oldLabel && <span className="block text-base font-medium text-muted-foreground line-through">{plans.monthly.oldLabel}</span>}
                {plans.monthly.label}
              </p>
            </div>
            <div className={cn(
              "w-6 h-6 rounded-full border-2 flex items-center justify-center",
              selectedPlan === 'monthly'
                ? "border-godyspo-gold bg-godyspo-gold"
                : "border-muted"
            )}>
              {selectedPlan === 'monthly' && <Check className="h-4 w-4 text-black" />}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Pay monthly, cancel anytime
          </p>
        </Card>

        {/* Annual Plan */}
        <Card
          className={cn(
            "p-6 cursor-pointer transition-all border-2 relative",
            selectedPlan === 'annual'
              ? "border-godyspo-gold bg-godyspo-gold/10"
              : "border-muted hover:border-godyspo-gold/50"
          )}
          onClick={() => onPlanChange('annual')}
        >
          {plans.annual.savings && (
            <div className="absolute -top-3 right-4 bg-godyspo-gold text-black text-xs font-bold px-3 py-1 rounded-full">
              Save {plans.annual.savings}
            </div>
          )}
          <div className="flex justify-between items-start mb-4">
            <div>
              <h4 className="text-lg font-semibold text-white">Annual</h4>
              <p className="text-2xl font-bold text-godyspo-gold mt-2">
                {plans.annual.oldLabel && <span className="block text-base font-medium text-muted-foreground line-through">{plans.annual.oldLabel}</span>}
                {plans.annual.label}
              </p>
            </div>
            <div className={cn(
              "w-6 h-6 rounded-full border-2 flex items-center justify-center",
              selectedPlan === 'annual'
                ? "border-godyspo-gold bg-godyspo-gold"
                : "border-muted"
            )}>
              {selectedPlan === 'annual' && <Check className="h-4 w-4 text-black" />}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Best value - save ${((plans.monthly.price * 12) - plans.annual.price).toFixed(2)}
          </p>
        </Card>
      </div>
    </div>
  );
};

export default PlanSelector;
